import { AuthPrincipalFetchFailure } from "lib/DetailedError";

import { decodeNodeId } from "lib/Relay";
import { ActiveProfileQuery } from "operations";
import { useAuthPrincipalQuery } from "./query";

export type AuthPrincipal = ReturnType<typeof patchProStatusIntoPrincipal>;
export type ActiveAccount = AuthPrincipal["account"];
export type ActiveUser = AuthPrincipal["user"];

/**
 * This hook is used when you need the active account or user information.
 * The accounts and users in the response will have encoded ids, ready to
 * be sent to atlas as query and mutation arguments.
 *
 * If the principal is used for display or integration purposes
 *  see: `useDecodedAuthPrincipal`
 */
export const useAuthPrincipal = () => {
  const { principal, loading, error } = useAuthPrincipalQuery();
  if (error || (!loading && !principal)) {
    throw new AuthPrincipalFetchFailure(error);
  }
  return principal && patchProStatusIntoPrincipal(principal);
};

/**
 * <h2>TODO(dan-muller): Remove this when pro status is included in Atlas schema</h2>
 * This logic is needed to hack in the pro status to the principal.
 * Once the Atlas schema supports this value, this logic should be removed.
 */
const patchProStatusIntoPrincipal = (principal: ActiveProfileQuery) => ({
  user: principal.user,
  account: {
    ...principal.account,
    isPro: true,
  },
  viewingAccounts: principal.otherAccounts.map((account) => ({
    ...account,
    isPro: true,
  })),
});

/**
 * This hook is used when you need a principal with decoded user and account ids.
 * For the most part this should be used when you need to integrate with
 * 1. a third party (ex. LaunchDarkly)
 * 1. if you want to display the id (ex. The header displays the account id)
 *
 * If the principal is used to get ids to send in queries or mutations
 *  see: `useAuthPrincipal`
 */
export const useDecodedAuthPrincipal = () => {
  const principal = useAuthPrincipal();
  return principal && decodePrincipal(principal);
};

const decodePrincipal = (principal: AuthPrincipal): AuthPrincipal => ({
  account: decodeNodeId(principal.account),
  user: decodeNodeId(principal.user),
  viewingAccounts: principal.viewingAccounts.map(decodeNodeId),
});
